import * as React from "react";
import IOrdersList, { TOrdersList } from "./IOrdersList";
import ConfirmForm from "@appComponents/ConfirmForm/ConfirmForm";
import SecondaryMarketService from "@services/SecondaryMarket.service";
import { IOrder } from "@market/interfaces/IOrder";
import { formatDate, numberWithDelimiter } from "@features/utils/functions";
import { noLeadingZeros } from "@market/features/helpers/functions";
import { Link } from "react-router-dom";
import { pushNotify } from "@appComponents/Notification/Notification";
import { updateOrders } from "@market/pages/MyOrdersPage/MyOrdersPage";
import ToolTip from "@appComponents/ToolTip/ToolTip";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import Big from "big.js";
import { connect } from "react-redux";
import { updateStoreOnCancelOrder } from "@market/features/action.market";
import { EordersTypes } from "@market/enums/OrdersTypes";
import Pagination from "@appComponents/Pagination/Pagination";
import { MAIN_CURRENCY } from "@bundles/consts";

import * as style from "./OrdersListStyle.module.scss";
import * as elements from "@appStyles/_elements.module.scss";
import * as general from "@appStyles/general.module.scss";
import * as tooltipStyle from "@appComponents/ToolTip/ToolTipStyle.module.scss";
const big = Big();

export class OrdersList extends React.Component<IOrdersList, TOrdersList> {
  state: TOrdersList = {
    currentPage: 1,
    maxPerPage: 10,
    myOrdersFilter: false,
    showLightbox: false,
  };

  onChange = () => {
    this.setState({ myOrdersFilter: !this.state.myOrdersFilter });
  };

  cancelOrderClick = (orderId: number, orderType: EordersTypes) => {
    this.setState({
      showLightbox: true,
      orderDataToCancel: { orderId, orderType },
    });
  };

  closeLightbox = () => {
    this.setState({ showLightbox: false });
  };

  cancelOrder = () => {
    if (this.state.orderDataToCancel) {
      const { orderId, orderType } = this.state.orderDataToCancel;
      const ApiCall =
        orderType === EordersTypes.sell
          ? SecondaryMarketService.cancelSellOrder(orderId)
          : SecondaryMarketService.cancelBuyOrder(orderId);

      ApiCall.then((res) => {
        if (!this.props.myOrdersList) {
          const { number_of_shares, id, asset_id } = res.data;
          this.props.updateStoreOnCancelOrder(
            number_of_shares,
            id,
            asset_id,
            orderType!
          );
        } else {
          updateOrders(res.data.id);
        }
        this.closeLightbox();
      }).catch((err) => {
        this.closeLightbox();
        pushNotify("error", "Something went wrong. Please try again.");
      });
    }
  };

  filterOrdersList = (data: IOrder[] = []) => {
    let orders = data;
    if (this.state.myOrdersFilter) {
      orders = data.filter((order: IOrder) => {
        return order.investor_id === this.props.investorId;
      });
    }

    return {
      orders: orders.slice(
        Math.max(0, this.state.currentPage - 1) * this.state.maxPerPage,
        this.state.currentPage * this.state.maxPerPage
      ),
      length: orders.length,
    };
  };

  updateCurrentPage = (page: number) => {
    this.setState({ currentPage: page });
  };

  renderButton = (order: IOrder) => {
    const basePath = `/companies/${this.props.companySlug}`;
    const buyUrl = `${basePath}/market/spvs/${this.props.spvId}/trades/buy/${order.id}`;
    const sellUrl = `${basePath}/market/spvs/${this.props.spvId}/trades/sell/${order.id}`;

    return this.props.investorId !== order.investor_id ? (
      this.props.type === EordersTypes.sell ? (
        <Link
          className={[elements.btn, elements.btnSuccess, style.tradeBtn].join(
            " "
          )}
          to={buyUrl}
          data-cy="buy"
        >
          Buy
        </Link>
      ) : this.props.sellingEnabled ? (
        <Link
          className={[
            elements.btn,
            elements.btnSuccess,
            style.tradeBtn,
            style.sellBtn,
          ].join(" ")}
          to={sellUrl}
          data-cy="sell"
        >
          Sell
        </Link>
      ) : (
        <ToolTip
          text={`You don’t have any [${order.spv_name}] shares yet to sell`}
          className={tooltipStyle.tooltipPurple}
        >
          <button
            className={[elements.btn, style.tradeBtn, style.btnDisabled].join(
              " "
            )}
            data-cy="sell"
          >
            Sell
          </button>
        </ToolTip>
      )
    ) : this.props.investorId === order.investor_id &&
      order.status === "sold_out" ? (
      <div className={style.soldOut}>
        {order.type === EordersTypes.buy ? "Bought Out" : "Sold Out"}
      </div>
    ) : this.props.investorId === order.investor_id &&
      order.status !== "suspended" &&
      order.number_of_available_shares > 0 ? (
      <button
        onClick={() => {
          this.cancelOrderClick(order.id, order.type);
        }}
        className={[elements.btn, style.btnCancel, style.tradeBtn].join(" ")}
        data-cy="cancel"
      >
        Cancel
      </button>
    ) : this.props.investorId === order.investor_id &&
      order.status !== "suspended" ? (
      <ToolTip text="Buy trade in progress" className={style.resetWhitespace}>
        <button
          className={[
            elements.btn,
            style.btnCancel,
            style.tradeBtn,
            style.btnDisabled,
          ].join(" ")}
          data-cy="cancel"
        >
          Cancel
        </button>
      </ToolTip>
    ) : null;
  };

  renderAmount = (order: IOrder) => {
    if (this.props.myOrdersList && order.status === "sold_out") {
      return (
        <>
          {numberWithDelimiter(noLeadingZeros(order.number_of_sold_shares))}
          <span className={style.amountInfo}>
            {" "}
            {order.type === EordersTypes.buy ? "bought" : "sold"}
          </span>
        </>
      );
    }

    return (
      <>
        {numberWithDelimiter(noLeadingZeros(order.number_of_available_shares))}
        {this.props.myOrdersList && order.status === "active" && (
          <span className={style.amountInfo}> left</span>
        )}
      </>
    );
  };

  getOrderValue = (order: IOrder) => {
    if (this.props.myOrdersList && order.status === "sold_out") {
      return big(order.number_of_sold_shares).times(order.price).toFixed(2);
    }

    return order.value;
  };

  renderTable = (orders: IOrder[]) => {
    const { myOrdersList } = this.props;

    return (
      <div className={style.tableContainer}>
        <div className={style.tableHead}>
          <div className={style.tableHeadRow}>
            {myOrdersList && <div className={style.tableHeader}>Name</div>}
            {myOrdersList && <div className={style.tableHeader}>Type</div>}
            <div className={style.tableHeader}>Price</div>
            <div className={style.tableHeader}>Amount</div>
            <div className={style.tableHeader}>Value</div>
            {myOrdersList && <div className={style.tableHeader}>Date</div>}
            <div className={style.tableHeader} />
          </div>
        </div>
        <div className={style.tableBody}>
          {orders.map((order: IOrder) => {
            return (
              <div key={order.id} className={style.tableRow}>
                {myOrdersList && (
                  <div className={style.tableItem}>{order.spv_name}</div>
                )}
                {myOrdersList && (
                  <div className={style.tableItem}>
                    {order.type.toUpperCase()}
                  </div>
                )}
                <div className={style.tableItem}>
                  <CurrencyElement
                    value={order.price}
                    currency={order.payment_currency}
                  />
                </div>
                <div className={style.tableItem}>
                  {this.renderAmount(order)}
                </div>
                <div className={style.tableItem}>
                  <CurrencyElement
                    value={this.getOrderValue(order)}
                    currency={order.payment_currency}
                  />
                </div>
                {myOrdersList && (
                  <div className={style.tableItem}>
                    {formatDate(order.created_at, true)}
                  </div>
                )}
                <div className={[style.tableItem, style.btnHolder].join(" ")}>
                  {this.renderButton(order)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  displayNoBuyOrders() {
    return (
      <>
        The price floor is&nbsp;
        <CurrencyElement
          value={this.props.priceFloor || 0}
          currency={MAIN_CURRENCY}
        />
        {this.props.isPlaceBuyOrderDisabled === false && (
          <>
            <br />
            <span className={style.makeOrder}>
              Click <Link to="orders/buy">here</Link> to place a buy order
            </span>
          </>
        )}
      </>
    );
  }

  displayNoSellOrders() {
    return (
      <>
        There are no sell orders
        {this.props.isPlaceSellOrderDisabled === false && (
          <>
            <br />
            {this.props.priceCap !== null &&
              this.props.priceCap !== undefined && (
                <>
                  The Price Cap is&nbsp;
                  <CurrencyElement
                    value={this.props.priceCap || 0}
                    currency={MAIN_CURRENCY}
                  />
                  <br />
                </>
              )}
            <span className={style.makeOrder}>
              Click <Link to="orders/sell">here</Link> to place a sell order
            </span>
          </>
        )}
      </>
    );
  }

  renderNoOrders(myOrdersFilter: boolean) {
    if (this.props.myOrdersList) {
      let label = "You have not placed any orders";

      if (this.props.filter) {
        label = `There are no ${this.props.filter} orders`;
      }

      return (
        <div className={style.noContentContainer}>
          <div className={style.iconContainer}>
            <svg className="svg-icon svg-icon--violet">
              <use xlinkHref="#icon-no_orders" />
            </svg>
          </div>
          <h4 data-cy="no-orders">{label}</h4>
        </div>
      );
    }

    const type = this.props.type === EordersTypes.buy ? "buy" : "sell";
    const label = myOrdersFilter
      ? "You have not placed any orders"
      : type === "buy"
      ? this.displayNoBuyOrders()
      : this.displayNoSellOrders();

    return (
      <div className={style.noContentContainer}>
        <div className={style.iconContainer}>
          <svg className="svg-icon svg-icon--violet">
            <use xlinkHref="#icon-no_orders" />
          </svg>
        </div>
        <h4 data-cy="no-orders">{label}</h4>
      </div>
    );
  }

  renderTopSection = () =>
    this.props.orders &&
    this.props.orders.length > 0 &&
    (!this.props.matchedOrders ? (
      <div className={general.clearfix}>
        {this.props.type && (
          <h2 className={style.ordersHeadline}>{this.props.type} Orders</h2>
        )}
        <div
          className={[
            elements.customCheckbox,
            elements.customCheckboxSmall,
            general.pY0,
            general.pullRight,
            style.checkbox,
          ].join(" ")}
        >
          {!this.props.myOrdersList && (
            <>
              <input
                type="checkbox"
                id={`filter${this.props.type}`}
                className={"test-filter-orders"}
                onChange={this.onChange}
              />
              <label
                className={style.myOrders}
                htmlFor={`filter${this.props.type}`}
              >
                Show my orders only
              </label>
            </>
          )}
        </div>
      </div>
    ) : (
      <p className={style.notice}>
        {this.props.type === EordersTypes.sell
          ? "Lower-priced sell orders are available. Select from the options below:"
          : "Higher buy orders are available. You can sell immediately at the best available price:"}
      </p>
    ));

  render() {
    const { orders, length } = this.filterOrdersList(this.props.orders);

    return (
      <div
        className={[
          style.ordersList,
          this.props.matchedOrders && style.ordersListMatchedOrders,
          !this.props.myOrdersList && style.ordersListHalf,
          this.props.myOrdersList && style.ordersListMyOrders,
        ].join(" ")}
      >
        {this.renderTopSection()}
        {orders.length > 0
          ? this.renderTable(orders)
          : this.renderNoOrders(this.state.myOrdersFilter)}
        <Pagination
          currentPage={this.state.currentPage}
          totalCount={length}
          maxPerPage={this.state.maxPerPage}
          filterData={this.updateCurrentPage}
        />
        <ConfirmForm
          closeLightbox={this.closeLightbox}
          actionFunction={this.cancelOrder}
          values={{
            headline: "Are you sure you wish to cancel your order?",
            paragraph: `Please note that if there are any pending trades related to your order, the shares
            would be deducted.`,
            actionButton: "Confirm",
          }}
          isVisible={this.state.showLightbox}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  return {
    updateStoreOnCancelOrder: (
      numberOfAvailableShares: number,
      orderId: number,
      assetId: number,
      orderType: EordersTypes
    ) => {
      dispatch(
        updateStoreOnCancelOrder(
          numberOfAvailableShares,
          orderId,
          assetId,
          orderType
        )
      );
    },
  };
};

export default connect(null, mapDispatchToProps)(OrdersList);
